import { SubmissionToolHeader } from '@/components/SubmissionToolHeader';
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  v2,
  type ValidationDiagnosticsRun,
  type ValidationResult,
  type ValidationResultEntry,
  type ValidationStatusResponse,
} from '@/api/v2Client';
import { ApiErrorNotice } from '@/components/shared/ApiErrorNotice';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { formatDateTime } from '@/lib/format';
import { summarizeValidationReport } from '@/lib/validationSummary';

// T-17 D10 + D11 — the page only READS persisted data when it opens. It used to
// POST the admin smoke test on every mount (twice under StrictMode), writing a
// fresh batch of ValidationResults rows each time and rendering "0 failed ·
// 0 passed" to someone who never ran anything. Running is now an explicit
// button; the rule-engine dry run is a separate, admin-only, clearly-unsaved
// panel that is never mixed into the persisted table.
type ReportState =
  | { status: 'loading' }
  | {
      status: 'ready';
      results: ReadonlyArray<ValidationResultEntry>;
      lastRunAt: string | null;
    }
  | { status: 'error'; error: unknown };

type DryRunState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'done'; results: ValidationResult[] }
  | { status: 'error'; error: unknown };

function severityColor(sev: ValidationResult['severity']): string {
  if (sev === 'error') return '#b91c1c';
  if (sev === 'warning') return '#b45309';
  return '#6b7280';
}

function statusBadge(status: ValidationResult['status']): { bg: string; fg: string; label: string } {
  if (status === 'pass') return { bg: '#dcfce7', fg: '#15803d', label: 'pass' };
  if (status === 'fail') return { bg: '#fee2e2', fg: '#b91c1c', label: 'fail' };
  return { bg: '#e5e7eb', fg: '#6b7280', label: 'skip' };
}

function StatusPill({ status }: { status: ValidationResult['status'] }) {
  const sb = statusBadge(status);
  return (
    <span
      style={{
        background: sb.bg,
        color: sb.fg,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {sb.label}
    </span>
  );
}

const btnStyle = (disabled: boolean, primary: boolean): CSSProperties => ({
  padding: '8px 16px',
  background: primary ? '#1B3A6B' : '#fff',
  color: primary ? '#fff' : '#1B3A6B',
  border: primary ? 'none' : '1px solid #1B3A6B',
  borderRadius: 6,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export function ValidationReportPage() {
  const { id = '' } = useParams<{ id: string }>();
  const perms = useUserPermissions();
  const canRun = perms.hasPermission('Documents', 'write');
  const canDiagnose = perms.hasPermission('Users', 'manage');

  const [state, setState] = useState<ReportState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<unknown>(null);
  const [dryRun, setDryRun] = useState<DryRunState>({ status: 'idle' });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setState({ status: 'loading' });
    Promise.all([v2.submissions.getValidationResults(id), v2.submissions.getValidationStatus(id)])
      .then(([res, status]: [{ results: ReadonlyArray<ValidationResultEntry> }, ValidationStatusResponse]) => {
        if (cancelled) return;
        setState({ status: 'ready', results: res.results, lastRunAt: status.lastRunAt });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'error', error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  const reload = useCallback((): void => setReloadKey((k) => k + 1), []);

  const runValidation = async (): Promise<void> => {
    setRunning(true);
    setRunError(null);
    try {
      await v2.submissions.validateNow(id);
      reload();
    } catch (err) {
      setRunError(err);
    } finally {
      setRunning(false);
    }
  };

  const runDiagnostics = async (): Promise<void> => {
    setDryRun({ status: 'running' });
    try {
      const out: ValidationDiagnosticsRun = await v2.validation.diagnostics(id);
      setDryRun({ status: 'done', results: out.results });
    } catch (err) {
      setDryRun({ status: 'error', error: err });
    }
  };

  const rows = state.status === 'ready' ? state.results : null;
  const { counts, ordered } = summarizeValidationReport(rows ?? []);

  const lastRun = state.status === 'ready' ? state.lastRunAt : null;
  const loading = state.status === 'loading';

  return (
    <section>
      {id && <SubmissionToolHeader id={id} />}
      <Link to={`/submissions/${id}`} style={{ color: '#1B3A6B' }}>
        ← Back to submission
      </Link>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 8,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0 }}>Validation report</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {canRun && (
            <button
              type="button"
              data-testid="validation-run-btn"
              onClick={() => void runValidation()}
              disabled={running || loading}
              style={btnStyle(running || loading, true)}
            >
              {running ? 'Running…' : 'Run validation'}
            </button>
          )}
          {canDiagnose && (
            <button
              type="button"
              data-testid="validation-diagnostics-btn"
              onClick={() => void runDiagnostics()}
              disabled={dryRun.status === 'running'}
              style={btnStyle(dryRun.status === 'running', false)}
            >
              Run rule diagnostics (dry run)
            </button>
          )}
        </div>
      </div>

      {runError !== null && (
        <ApiErrorNotice error={runError} fallback="Failed to run validation" />
      )}
      {loading && <p>Loading validation results…</p>}
      {state.status === 'error' && (
        <ApiErrorNotice
          error={state.error}
          fallback="Failed to load validation results"
          onRetry={reload}
        />
      )}

      {state.status === 'ready' && ordered.length === 0 && (
        <p data-testid="validation-not-run" style={{ color: '#6b7280' }}>
          Not run yet — validation has not been run on this submission.
        </p>
      )}

      {state.status === 'ready' && ordered.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              padding: '16px 0 4px',
              fontSize: 14,
              color: '#1f2937',
            }}
          >
            <strong>{counts.fail}</strong>
            <span>failed</span>
            <span style={{ color: '#d1d5db' }}>·</span>
            <strong>{counts.pass}</strong>
            <span>passed</span>
            <span style={{ color: '#d1d5db' }}>·</span>
            <strong>{counts.skip}</strong>
            <span>skipped</span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
            Last run {lastRun ? formatDateTime(lastRun) : '—'}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>Rule</th>
                <th style={{ padding: 8 }}>Target</th>
                <th style={{ padding: 8 }}>Severity</th>
                <th style={{ padding: 8 }}>Status</th>
                <th style={{ padding: 8 }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.resultId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: 8, fontFamily: 'monospace' }}>{r.ruleName}</td>
                  <td style={{ padding: 8, fontSize: 13 }}>
                    {r.targetType}
                    {r.targetType === 'Document' && (
                      <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>
                        {' '}
                        {r.targetId}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 8, color: severityColor(r.severity) }}>{r.severity}</td>
                  <td style={{ padding: 8 }}>
                    <StatusPill status={r.status} />
                  </td>
                  <td style={{ padding: 8, color: '#374151', fontSize: 13 }}>
                    {r.message ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {dryRun.status !== 'idle' && (
        <div
          style={{
            marginTop: 24,
            padding: 12,
            border: '1px dashed #9ca3af',
            borderRadius: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong>Rule diagnostics</strong>
            <span
              data-testid="validation-dry-run-badge"
              style={{
                background: '#fef3c7',
                color: '#92400e',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Dry run — not saved
            </span>
          </div>
          {dryRun.status === 'running' && <p>Running rules…</p>}
          {dryRun.status === 'error' && (
            <ApiErrorNotice error={dryRun.error} fallback="Failed to run rule diagnostics" />
          )}
          {dryRun.status === 'done' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Rule</th>
                  <th style={{ padding: 8 }}>Severity</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {dryRun.results.map((r, i) => (
                  <tr key={`${r.ruleName}-${i}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 8, fontFamily: 'monospace' }}>{r.ruleName}</td>
                    <td style={{ padding: 8, color: severityColor(r.severity) }}>{r.severity}</td>
                    <td style={{ padding: 8 }}>
                      <StatusPill status={r.status} />
                    </td>
                    <td style={{ padding: 8, color: '#374151', fontSize: 13 }}>
                      {r.message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
